/**
 * AudioManager — narracao, SFX e trilha.
 *
 * ESTADO ATUAL: os arquivos de narracao ainda nao foram gravados. Enquanto
 * conteudo/narracao.json tiver "audio": null, este modulo:
 *   - NAO inventa voz sintetica (decisao do responsavel: a voz vem da ElevenLabs)
 *   - avisa alto no console, em portugues, qual audio faltou
 *   - deixa a legenda na tela e o jogo seguir, para dar para testar a mecanica
 *
 * D5 so estara cumprida quando os audios existirem. Ate la, o jogo NAO deve ser
 * testado com as criancas — a instrucao estaria chegando so por texto.
 *
 * Os SFX (toque, sucesso) sao tons sintetizados no WebAudio. Isso nao e
 * conteudo pedagogico: e o retorno sonoro de D4.
 */

const CAMINHO_AUDIO = '/assets/audio';

export class AudioManager {
  constructor() {
    this.contexto = null;
    this.narracaoAtual = null;
    this.faltantesJaAvisados = new Set();
  }

  /**
   * O navegador so libera audio depois de um gesto do usuario.
   * Chamado no primeiro toque da tela.
   */
  destravar() {
    if (this.contexto) return;
    const Contexto = window.AudioContext ?? window.webkitAudioContext;
    if (!Contexto) {
      console.warn('FAZENDA: este navegador nao tem WebAudio. O jogo roda, mas sem som.');
      return;
    }
    this.contexto = new Contexto();
  }

  /**
   * Toca uma narracao. `narracao` e o objeto { texto, audio } do narracao.json.
   * Devolve uma promessa que resolve quando o audio termina (ou na hora, se
   * ainda nao houver audio gravado).
   */
  async narrar(id, narracao) {
    this.pararNarracao();

    if (!narracao?.audio) {
      if (!this.faltantesJaAvisados.has(id)) {
        this.faltantesJaAvisados.add(id);
        console.warn(
          `FAZENDA: a narracao "${id}" ainda nao tem audio gravado. ` +
            'A legenda aparece na tela, mas D5 exige audio + demonstracao visual. ' +
            `Grave o audio, salve em ${CAMINHO_AUDIO} e preencha o campo "audio" ` +
            'de conteudo/narracao.json.'
        );
      }
      return;
    }

    const elemento = new Audio(`${CAMINHO_AUDIO}/${narracao.audio}`);
    this.narracaoAtual = elemento;

    await new Promise((resolver) => {
      elemento.addEventListener('ended', resolver, { once: true });
      elemento.addEventListener(
        'error',
        () => {
          // P5: falha de asset aparece, nao some.
          console.error(
            `FAZENDA: nao consegui tocar o audio "${narracao.audio}" da narracao "${id}". ` +
              `Confira se o arquivo existe em ${CAMINHO_AUDIO}.`
          );
          resolver();
        },
        { once: true }
      );
      elemento.play().catch((e) => {
        console.error(
          `FAZENDA: o navegador bloqueou o audio da narracao "${id}": ${e.message}. ` +
            'Isso costuma acontecer quando o som toca antes do primeiro toque na tela.'
        );
        resolver();
      });
    });

    this.narracaoAtual = null;
  }

  pararNarracao() {
    if (this.narracaoAtual) {
      this.narracaoAtual.pause();
      this.narracaoAtual = null;
    }
  }

  /** SFX de toque: nota curta e macia. D4 — retorno imediato. */
  tocarToque(passo = 0) {
    // Escala pentatonica: sobe conforme a crianca conta, sem soar errado nunca.
    const escala = [523.25, 587.33, 659.25, 783.99, 880.0];
    this.tom(escala[passo % escala.length], 0.12, 0.18);
  }

  /** SFX de conclusao: dois tons subindo. Nunca ha som de erro (D6). */
  tocarSucesso() {
    this.tom(659.25, 0.14, 0.2);
    setTimeout(() => this.tom(987.77, 0.22, 0.2), 130);
  }

  /** D6: "errado" nao existe. Isto e um convite macio a tentar de novo. */
  tocarTentarDeNovo() {
    this.tom(440.0, 0.18, 0.12);
  }

  tom(frequencia, duracao, volume) {
    if (!this.contexto) return;
    const osc = this.contexto.createOscillator();
    const ganho = this.contexto.createGain();

    osc.type = 'sine';
    osc.frequency.value = frequencia;

    const agora = this.contexto.currentTime;
    ganho.gain.setValueAtTime(0, agora);
    ganho.gain.linearRampToValueAtTime(volume, agora + 0.02);
    ganho.gain.exponentialRampToValueAtTime(0.001, agora + duracao);

    osc.connect(ganho).connect(this.contexto.destination);
    osc.start(agora);
    osc.stop(agora + duracao);
  }
}
